import { useNavigate } from 'react-router-dom';
import { Search, Compass, ClipboardList, DollarSign, FileCheck, Rocket, Monitor, Sun, Sunrise, CalendarDays, Zap, Clock, ArrowRight } from 'lucide-react';

const STEPS = [
  { n: '01', icon: Search, title: 'Learn about your goals', body: 'A conversation, not a form. We start where you are.' },
  { n: '02', icon: Compass, title: 'Explore suitable education options', body: 'Pathways that fit your life, budget, and ambitions.' },
  { n: '03', icon: ClipboardList, title: 'Compare programs and institutions', body: 'Cost, outcomes, side-by-side. No marketing fluff.' },
  { n: '04', icon: DollarSign, title: 'Understand tuition and funding', body: 'Loans, grants, sponsorships — in plain English.' },
  { n: '05', icon: FileCheck, title: 'Complete your application', body: 'Paperwork handled. You focus on what comes next.' },
  { n: '06', icon: Rocket, title: 'Prepare for your first day of class', body: "You're not on your own after admission." },
];

const MODES = [
  { icon: Sun, label: 'Full-Time', body: 'Traditional in-class programs.' },
  { icon: Sunrise, label: 'Evening', body: 'For working professionals.' },
  { icon: CalendarDays, label: 'Weekend', body: 'Family-friendly schedules.' },
  { icon: Monitor, label: 'Online', body: 'Learn from anywhere in Canada.' },
  { icon: Zap, label: 'Hybrid', body: 'Best of both worlds.' },
  { icon: Clock, label: 'Accelerated', body: 'Finish faster, start sooner.' },
];

const FUNDING = [
  ['Tuition Planning', 'Understand total cost of ownership, upfront.'],
  ['Financial Assistance', 'Government loans and provincial aid.'],
  ['Scholarships', 'Institution-specific and merit-based awards.'],
  ['Employer Sponsorship', "How to ask your employer to pay for it."],
];

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="services-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-4">How we help</div>
        <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[1.05] tracking-tight">
          Guidance from goal <span className="text-brand-pine italic">to first day of class.</span>
        </h1>
        <p className="mt-8 text-lg text-brand-slate leading-relaxed max-w-2xl">
          Six steps. One advisor. No hand-offs, no queues, no pressure. Here&apos;s what working with us actually looks like.
        </p>
      </section>

      <section className="bg-brand-obsidian text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-6" data-testid={`services-step-${s.n}`}>
                <div className="w-12 h-12 rounded-full bg-brand-coral flex items-center justify-center flex-shrink-0">
                  <s.icon size={18} />
                </div>
                <div>
                  <div className="font-serif-editorial text-4xl text-white/10 leading-none">{s.n}</div>
                  <h3 className="font-serif-editorial text-2xl mt-3 leading-snug">{s.title}</h3>
                  <p className="text-white/60 mt-3 leading-relaxed max-w-md">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="online" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Learning options</div>
        <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight max-w-xl leading-tight">Programs shaped around your week.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-brand-border/40 border border-brand-border/40 rounded-2xl overflow-hidden mt-14">
          {MODES.map((m) => (
            <div key={m.label} className="bg-white p-8">
              <m.icon size={24} className="text-brand-coral" />
              <div className="font-serif-editorial text-lg mt-5 leading-snug">{m.label}</div>
              <div className="text-xs text-brand-slate mt-2">{m.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-brand-border/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Funding</div>
          <h2 className="font-serif-editorial text-3xl md:text-5xl tracking-tight max-w-xl leading-tight">Can you afford it? Almost certainly, yes.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {FUNDING.map(([t, d]) => (
              <div key={t} className="border border-brand-border/50 rounded-2xl p-6">
                <h3 className="font-serif-editorial text-lg leading-snug">{t}</h3>
                <p className="text-xs text-brand-slate mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-pine text-white">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif-editorial text-3xl md:text-5xl leading-tight">Ready to see what your options are?</h2>
          <button
            onClick={() => navigate('/contact')}
            data-testid="services-cta"
            className="mt-8 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-8 py-4 rounded-full transition-colors"
          >
            Book a Free Consultation <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}
